import { useState, useEffect } from 'react';
import { transactionsAPI, exportAPI, downloadBlob } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Download,
  Trash2,
  FileSpreadsheet,
  FileText,
  Loader2,
  Building,
  CreditCard,
  Wallet,
  Smartphone,
  Banknote
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const CATEGORIES = {
  entrada: ['Vendas', 'Contratos', 'Serviços', 'Outros'],
  saida: ['Salários', 'Aluguel', 'Fornecedores', 'Marketing', 'Impostos', 'Equipamentos', 'Outros']
};

const PAYMENT_METHODS = [
  { value: 'banco', label: 'Transferência Bancária', icon: Building },
  { value: 'pix', label: 'PIX', icon: Smartphone },
  { value: 'cartao', label: 'Cartão', icon: CreditCard },
  { value: 'dinheiro', label: 'Dinheiro', icon: Banknote }
];

const CHART_COLORS = ['#1e3a8a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const INITIAL_FORM = {
  type: 'entrada',
  amount: '',
  description: '',
  category: '',
  payment_method: 'pix',
  bank_name: '',
  agency: '',
  account: '',
  date: new Date().toISOString().split('T')[0]
};

export const Financial = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [activeTab, setActiveTab] = useState('all');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear, activeTab]);

  const loadData = async () => {
    try {
      const params = {};
      if (selectedMonth) params.month = parseInt(selectedMonth);
      if (selectedYear) params.year = parseInt(selectedYear);
      if (activeTab !== 'all') params.type = activeTab;

      const [transRes, summaryRes] = await Promise.all([
        transactionsAPI.list(params),
        transactionsAPI.getSummary({ year: parseInt(selectedYear) })
      ]);

      setTransactions(transRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await transactionsAPI.create({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Transação registrada com sucesso!');
      setDialogOpen(false);
      setFormData(INITIAL_FORM);
      loadData();
    } catch (error) {
      toast.error('Erro ao registrar transação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta transação?')) return;
    
    try {
      await transactionsAPI.delete(id);
      toast.success('Transação excluída!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir transação');
    }
  };

  const handleExport = async (format) => {
    try {
      const params = { format };
      if (selectedMonth) params.month = parseInt(selectedMonth);
      if (selectedYear) params.year = parseInt(selectedYear);
      
      const { data } = await exportAPI.transactions(params);
      downloadBlob(data, `transacoes.${format}`);
      toast.success('Exportação concluída!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getPaymentMethodIcon = (method) => {
    const pm = PAYMENT_METHODS.find(p => p.value === method);
    return pm ? pm.icon : Wallet;
  };

  // Prepare chart data
  const monthlyChartData = summary?.by_month
    ? Object.entries(summary.by_month)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, data]) => {
          const [year, month] = key.split('-');
          return {
            name: MONTHS_SHORT[parseInt(month) - 1],
            entradas: data.entradas,
            saidas: data.saidas,
            saldo: data.entradas - data.saidas
          };
        })
    : [];

  const categoryData = summary?.by_category
    ? Object.entries(summary.by_category)
        .filter(([_, value]) => value > 0)
        .map(([category, value], index) => ({
          name: category,
          value,
          color: CHART_COLORS[index % CHART_COLORS.length]
        }))
    : [];

  return (
    <div className="space-y-6" data-testid="financial-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Financeiro</h1>
          <p className="text-slate-500 mt-1">Gerencie entradas e saídas da empresa</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-transaction-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Transação
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="kpi-card success" data-testid="total-entradas">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Total de Entradas</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {formatCurrency(summary?.total_entradas)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="kpi-card danger" data-testid="total-saidas">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Total de Saídas</p>
              <p className="text-3xl font-bold text-red-600 mt-1">
                {formatCurrency(summary?.total_saidas)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className={`kpi-card ${summary?.saldo >= 0 ? '' : 'danger'}`} data-testid="saldo">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 font-medium">Saldo</p>
              <p className={`text-3xl font-bold mt-1 ${summary?.saldo >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {formatCurrency(summary?.saldo)}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${summary?.saldo >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
              <DollarSign className={`w-6 h-6 ${summary?.saldo >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200" data-testid="monthly-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Entradas vs Saídas ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white'
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Bar dataKey="entradas" fill="#10b981" radius={[4, 4, 0, 0]} name="Entradas" />
                  <Bar dataKey="saidas" fill="#ef4444" radius={[4, 4, 0, 0]} name="Saídas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="category-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Despesas por Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                      formatter={(value) => [formatCurrency(value), '']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Sem dados de despesas
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Transactions */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold text-slate-900">
              Transações
            </CardTitle>
            <div className="flex gap-2">
              <Select value={selectedMonth || "all"} onValueChange={(val) => setSelectedMonth(val === "all" ? "" : val)}>
                <SelectTrigger className="w-36" data-testid="month-filter">
                  <SelectValue placeholder="Todos os meses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os meses</SelectItem>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-28" data-testid="year-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all" data-testid="tab-all">Todas</TabsTrigger>
              <TabsTrigger value="entrada" data-testid="tab-entrada">Entradas</TabsTrigger>
              <TabsTrigger value="saida" data-testid="tab-saida">Saídas</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <DollarSign className="w-12 h-12 mb-4" />
                  <p className="text-lg font-medium">Nenhuma transação encontrada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Descrição</th>
                        <th>Categoria</th>
                        <th>Método</th>
                        <th>Data</th>
                        <th>Valor</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction) => {
                        const PaymentIcon = getPaymentMethodIcon(transaction.payment_method);
                        return (
                          <tr key={transaction.id} data-testid={`transaction-row-${transaction.id}`}>
                            <td>
                              {transaction.type === 'entrada' ? (
                                <span className="inline-flex items-center gap-1 text-green-600">
                                  <TrendingUp className="w-4 h-4" />
                                  Entrada
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-600">
                                  <TrendingDown className="w-4 h-4" />
                                  Saída
                                </span>
                              )}
                            </td>
                            <td className="text-slate-900 font-medium">{transaction.description}</td>
                            <td className="text-slate-600">{transaction.category}</td>
                            <td>
                              <span className="inline-flex items-center gap-1 text-slate-600">
                                <PaymentIcon className="w-4 h-4" />
                                {PAYMENT_METHODS.find(p => p.value === transaction.payment_method)?.label || transaction.payment_method}
                              </span>
                            </td>
                            <td className="text-slate-600">
                              {new Date(transaction.date).toLocaleDateString('pt-BR')}
                            </td>
                            <td className={`font-bold ${transaction.type === 'entrada' ? 'text-green-600' : 'text-red-600'}`}>
                              {transaction.type === 'entrada' ? '+' : '-'} {formatCurrency(transaction.amount)}
                            </td>
                            <td>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(transaction.id)}
                                data-testid={`delete-transaction-${transaction.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nova Transação</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Transação</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={formData.type === 'entrada' ? 'default' : 'outline'}
                  className={formData.type === 'entrada' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setFormData({ ...formData, type: 'entrada', category: '' })}
                  data-testid="type-entrada"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Entrada
                </Button>
                <Button
                  type="button"
                  variant={formData.type === 'saida' ? 'default' : 'outline'}
                  className={formData.type === 'saida' ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setFormData({ ...formData, type: 'saida', category: '' })}
                  data-testid="type-saida"
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  Saída
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0,00"
                  required
                  data-testid="input-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="input-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva a transação..."
                required
                data-testid="input-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES[formData.type].map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Método de Pagamento *</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger data-testid="select-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((pm) => (
                      <SelectItem key={pm.value} value={pm.value}>
                        <span className="flex items-center gap-2">
                          <pm.icon className="w-4 h-4" />
                          {pm.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.payment_method === 'banco' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Banco</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="Nome do banco"
                    data-testid="input-bank"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Agência</Label>
                  <Input
                    value={formData.agency}
                    onChange={(e) => setFormData({ ...formData, agency: e.target.value })}
                    placeholder="0000"
                    data-testid="input-agency"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conta</Label>
                  <Input
                    value={formData.account}
                    onChange={(e) => setFormData({ ...formData, account: e.target.value })}
                    placeholder="00000-0"
                    data-testid="input-account"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                className={formData.type === 'entrada' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                disabled={submitting}
                data-testid="submit-transaction-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Registrar {formData.type === 'entrada' ? 'Entrada' : 'Saída'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
